import { type Dispatch } from '@reduxjs/toolkit';
import { MonarchClient } from '@redventures/monarch-sdk';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { parseQueryParams } from '@core/context/FeatureFlagsContext';
import { setTestResults } from '@core/reducers/monarchSlice';
import { newRelicNoticeError, nrErrorMap } from '@core/services/newRelic';
import { type RootState } from '@core/store';

import { type MonarchRequest, type MonarchParams, type Rules } from './monarch';
import getMonarchRequests from './monarch.requests';

// Maps method from monarch config to sdk
const monarchMethodMap = {
  rule: 'evaluateRule',
  ruleset: 'evaluateRuleSet',
};

export default () => {
  const { degree, category, subject, publisher: searchParamPublisher, ...queryParams } = parseQueryParams();

  const publisher = searchParamPublisher || window?.HE?.publisher?.name || window?.location?.host?.replace('www.', '');

  const isAffiliate = window?.location?.host?.includes('discover.');

  // Dispatch events
  const dispatch: Dispatch = useDispatch();
  // Current page of the flow
  const { pathname }: { pathname: string } = useLocation();

  // Store state
  const storeState: RootState = useSelector((state: RootState) => state);

  // Arguments to pass to monarch config
  const monarchParams: MonarchParams = {
    state: storeState,
    slug: pathname,
  };

  // Monarch Requests
  const monarchRequests: MonarchRequest[] = getMonarchRequests(monarchParams);
  // Monarch results to return from hook
  const [results, setResults] = useState<Partial<Rules>>({});

  /* watchlist - conditions that have onChange, we need to watch for changes in the value that is passed
  key: Test Id
  value: the value you want to watch
  */
  const [watchList, setWatchList] = useState<Record<string, unknown>>({});

  // Payload Defaults to pass in each Monarch request
  const payloadDefaults: Record<string, any> = {
    publisher,
    isAffiliate,
    degree,
    category,
    subject,
    ...queryParams,
  };
  // Param Defualts to pass in each Monarch request
  // https://redventures.atlassian.net/wiki/spaces/COHSN/pages/1427145833/Client+API+-+Monarch
  const paramDefaults: Record<string, string> = {
    sessionId: window._Cohesion?.sessionId,
    anonymousId: window._Cohesion?.anonymousId,
    instanceId: window._Cohesion?.instanceId,
    makeSourceUid: window._Cohesion?.sourceKey,
    makeIdentifier: window._Cohesion?.anonymousId,
    writeKeyOverride: window._Cohesion?.writeKey,
  };

  const handleMonarchRules = async (): Promise<void> => {
    try {
      // Init rule engine
      const rulesEngine: MonarchClient = new MonarchClient(
        import.meta.env.VITE_MONARCH_SRC_KEY,
        import.meta.env.VITE_MONARCH_CLIENT_TOKEN,
        {
          timeout: 5000,
        }
      );
      // local variables for testResults & submittedList
      const rlts: Record<string, unknown> = {};

      // Array of monarch engine calls
      const monarchPromises = monarchRequests.map(
        ({ condition: isConditionMet = false, target, type, id, payload = {}, params = {}, then: thenFunction }) => {
          if (isConditionMet === 'onChange') {
            if (watchList[id] !== target) {
              setWatchList({ ...watchList, [id]: target });
            } else return undefined;
          } else if (!isConditionMet || results[id] !== undefined) return undefined;

          return rulesEngine[monarchMethodMap[type]](
            id,
            { ...payloadDefaults, ...payload },
            { ...paramDefaults, ...params }
          )
            .then(thenFunction)
            .catch(() => undefined);
        }
      );

      // Await all monarch requests
      const monarchResults: Record<keyof Rules, unknown>[] = await Promise.all(monarchPromises);

      // Loop through monarch results
      monarchResults.forEach((result, i) => {
        const { id } = monarchRequests[i];
        // amp the result for later storage
        if (result !== undefined) {
          rlts[id] = result;
        }
      });

      // Prevent unnecessary rerendering from state updates
      if (JSON.stringify(rlts) !== '{}') {
        // merge old results with incoming results
        const allResults = { ...results, ...rlts };
        // local use
        setResults(() => allResults);

        // Keep in store
        dispatch(setTestResults(allResults));
      }
    } catch (err) {
      newRelicNoticeError(nrErrorMap.MONARCH_CLIENT, err as Error);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(handleMonarchRules, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [pathname, storeState.inputs, window?.cohesion]);

  return results;
};
